import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TalentureLogoStandalone } from "@/components/TalentureLogo";
import { Menu, X, User, Building2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePublicMarketEnabled } from "@/hooks/usePublicMarketEnabled";
import { getAppUrl, isMarketingSite } from "@/utils/subdomain";
import { FeaturesNavDropdown, FeaturesNavMobile } from "@/components/website/FeaturesNav";

interface WebsiteLayoutProps {
  children: React.ReactNode;
  variant?: "company" | "candidate";
}

function WebsiteHeader({ variant = "company" }: { variant?: "company" | "candidate" }) {
  const [, setLocation] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const cvMarketEnabled = usePublicMarketEnabled();

  const isCompany = variant === "company";

  const getDashboardPath = () => {
    if (user?.role === 'admin') return '/system/dashboard';
    if (user?.role === 'candidate') return '/candidate-dashboard';
    if (user?.role === 'company') return '/company-dashboard';
    return '/';
  };

  const handleDashboardClick = () => {
    const path = getDashboardPath();
    const appUrl = getAppUrl(path);
    
    // If cross-subdomain navigation needed (different URL), use window.location
    if (appUrl !== path) {
      window.location.href = appUrl;
    } else {
      setLocation(path);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link href={isAuthenticated ? getDashboardPath() : (isCompany ? "/" : "/candidates")} aria-label="Talenture home" data-testid="link-logo">
              <TalentureLogoStandalone className="cursor-pointer" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
              {isCompany ? (
                <>
                  <FeaturesNavDropdown variant="company" />
                  <Link href="/company/pricing" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-pricing">
                    Pricing
                  </Link>
                  <Link href="/connect-your-ats" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-integrations">
                    Integrations
                  </Link>
                </>
              ) : (
                <>
                  <FeaturesNavDropdown variant="candidate" />
                  <a href="/candidates#how-it-works" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-how-it-works">
                    How it works
                  </a>
                  <a href="/candidates#benefits" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-benefits">
                    Benefits
                  </a>
                  <Link href="/jobs" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-browse-jobs">
                    Browse Jobs
                  </Link>
                  {cvMarketEnabled && (
                    <Link href="/cv-vs-market" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" data-testid="link-cv-vs-market">
                      Free CV Check
                    </Link>
                  )}
                </>
              )}
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {!isAuthenticated && (
              <ToggleGroup 
                type="single" 
                value={isCompany ? "company" : "candidate"}
                className="bg-muted rounded-lg p-1"
                aria-label="View site as"
                data-testid="toggle-audience-switch"
              >
                <ToggleGroupItem 
                  value="candidate" 
                  onClick={() => setLocation("/candidates")}
                  className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                  data-testid="toggle-candidate"
                >
                  <User className="h-3.5 w-3.5 mr-1.5" />
                  Candidate
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="company" 
                  onClick={() => setLocation("/")}
                  className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                  data-testid="toggle-company"
                >
                  <Building2 className="h-3.5 w-3.5 mr-1.5" />
                  Company
                </ToggleGroupItem>
              </ToggleGroup>
            )}
            {isAuthenticated ? (
              <Button
                onClick={handleDashboardClick}
                data-testid="button-go-to-dashboard"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setLocation(isCompany ? "/company-auth" : "/candidate-auth")}
                  data-testid="button-login"
                >
                  Login
                </Button>
                <Button
                  onClick={() => setLocation(isCompany ? "/company-auth" : "/candidate-auth")}
                  data-testid="button-get-started"
                >
                  {isCompany ? "Claim your free role" : "Get started"}
                </Button>
              </>
            )}
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav-menu"
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div id="mobile-nav-menu" className="md:hidden py-4 border-t border-gray-200 dark:border-gray-800">
            <nav className="flex flex-col gap-4" aria-label="Mobile navigation">
              {isCompany ? (
                <>
                  <FeaturesNavMobile variant="company" onNavigate={() => setMobileMenuOpen(false)} />
                  <Link href="/company/pricing" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
                    Pricing
                  </Link>
                  <Link href="/connect-your-ats" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
                    Integrations
                  </Link>
                </>
              ) : (
                <>
                  <FeaturesNavMobile variant="candidate" onNavigate={() => setMobileMenuOpen(false)} />
                  <a href="/candidates#how-it-works" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
                    How it works
                  </a>
                  <a href="/candidates#benefits" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
                    Benefits
                  </a>
                  <Link href="/jobs" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
                    Browse Jobs
                  </Link>
                  {cvMarketEnabled && (
                    <Link href="/cv-vs-market" className="text-sm text-gray-600 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)} data-testid="link-cv-vs-market-mobile">
                      Free CV Check
                    </Link>
                  )}
                </>
              )}
              <hr className="border-gray-200 dark:border-gray-700" />
              {!isAuthenticated && (
                <div className="py-2">
                  <p id="audience-label-mobile" className="text-xs text-muted-foreground mb-2">I am a:</p>
                  <ToggleGroup 
                    type="single" 
                    value={isCompany ? "company" : "candidate"}
                    className="bg-muted rounded-lg p-1 justify-start"
                    aria-labelledby="audience-label-mobile"
                    data-testid="toggle-audience-switch-mobile"
                  >
                    <ToggleGroupItem 
                      value="candidate" 
                      onClick={() => { setLocation("/candidates"); setMobileMenuOpen(false); }}
                      className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                      data-testid="toggle-candidate-mobile"
                    >
                      <User className="h-3.5 w-3.5 mr-1.5" />
                      Candidate
                    </ToggleGroupItem>
                    <ToggleGroupItem 
                      value="company" 
                      onClick={() => { setLocation("/"); setMobileMenuOpen(false); }}
                      className="text-xs px-3 py-1.5 data-[state=on]:bg-background data-[state=on]:shadow-sm rounded-md"
                      data-testid="toggle-company-mobile"
                    >
                      <Building2 className="h-3.5 w-3.5 mr-1.5" />
                      Company
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              )}
              {isAuthenticated ? (
                <Button
                  className="justify-start"
                  onClick={() => { handleDashboardClick(); setMobileMenuOpen(false); }}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => { setLocation(isCompany ? "/company-auth" : "/candidate-auth"); setMobileMenuOpen(false); }}
                  >
                    Login
                  </Button>
                  <Button
                    className="justify-start"
                    onClick={() => { setLocation(isCompany ? "/company-auth" : "/candidate-auth"); setMobileMenuOpen(false); }}
                  >
                    {isCompany ? "Claim your free role" : "Get started"}
                  </Button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

function WebsiteFooter() {
  const cvMarketEnabled = usePublicMarketEnabled();
  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-8">
          <div className="col-span-1 md:col-span-2 text-left">
            <div className="flex justify-start">
              <TalentureLogoStandalone className="mb-4" />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
              AI-powered candidate engagement. We use AI and smart automation so you can spend more time for the human connection.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/company/about" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-about">
                  About
                </Link>
              </li>
              <li>
                <Link href="/company/contact" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-contact">
                  Contact
                </Link>
              </li>
              <li>
                <Link href="/jobs" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-jobs">
                  Browse Jobs
                </Link>
              </li>
              {cvMarketEnabled && (
                <li>
                  <Link href="/cv-vs-market" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-cv-vs-market">
                    Free CV Check
                  </Link>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/legal/privacy" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/legal/terms" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-terms">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link href="/legal/cookies" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-cookies">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link href="/legal/security" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-security">
                  Security
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Compare</h3>
            <ul className="space-y-2">
              <li>
                <a href="/compare/workable-alternative" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-compare-workable">
                  Workable alternative
                </a>
              </li>
              <li>
                <a href="/compare/ashby-alternative" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-compare-ashby">
                  Ashby alternative
                </a>
              </li>
              <li>
                <a href="/compare/seekout-alternative" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-compare-seekout">
                  SeekOut alternative
                </a>
              </li>
              <li>
                <a href="/compare/dover-alternative" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-compare-dover">
                  Dover alternative
                </a>
              </li>
              <li>
                <a href="/compare" className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium" aria-label="More compare alternatives" data-testid="link-footer-compare-all">
                  More →
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Salary Guide</h3>
            <ul className="space-y-2">
              <li>
                <a href="/salary/ai-engineer" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-salary-ai">
                  AI Engineer salaries
                </a>
              </li>
              <li>
                <a href="/salary/software-engineer" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-salary-swe">
                  Software Engineer salaries
                </a>
              </li>
              <li>
                <a href="/salary/data-engineer" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-salary-data">
                  Data Engineer salaries
                </a>
              </li>
              <li>
                <a href="/salary/devops-engineer" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="link-footer-salary-devops">
                  DevOps Engineer salaries
                </a>
              </li>
              <li>
                <a href="/salary" className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium" aria-label="More salary guides" data-testid="link-footer-salary-all">
                  More →
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            © {new Date().getFullYear()} talenture.ai. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export function WebsiteLayout({ children, variant = "company" }: WebsiteLayoutProps) {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:bg-white focus:text-primary focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-primary"
      >
        Skip to main content
      </a>
      <WebsiteHeader variant={variant} />
      <main id="main-content" className="flex-1">
        {children}
      </main>
      <WebsiteFooter />
    </div>
  );
}

export default WebsiteLayout;
